/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import React, { useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { BookOpen, Eye } from 'lucide-react';

interface FretboardLearnProps {
  // We'll use these to force the fretboard into a clean state
  onSetKey: (key: string) => void;
  onSetScale: (scale: string) => void;
  onSetShowNoteNames: (show: boolean) => void;
  currentKey: string;
  currentScale: string;
  showNoteNames: boolean;
}

const FretboardLearn: React.FC<FretboardLearnProps> = ({
  onSetKey,
  onSetScale,
  onSetShowNoteNames,
  currentKey,
  currentScale,
  showNoteNames
}) => {
  
  // Set fretboard to clean state when this component mounts
  useEffect(() => {
    onSetKey('C');
    onSetScale('Ionian');
    onSetShowNoteNames(true);
  }, [onSetKey, onSetScale, onSetShowNoteNames]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <BookOpen className="h-5 w-5" />
          Learn the Fretboard
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Current Settings Display */}
        <Card className="p-4 bg-blue-50 border-blue-200">
          <div className="space-y-2">
            <h4 className="font-semibold text-blue-900 flex items-center gap-2">
              <Eye className="h-4 w-4" />
              Fretboard Display
            </h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium">Key:</span> C
              </div>
              <div>
                <span className="font-medium">Scale:</span> Ionian (Natural)
              </div>
              <div>
                <span className="font-medium">Note Names:</span> Visible
              </div>
              <div>
                <span className="font-medium">Mode:</span> Study Mode
              </div>
            </div>
          </div>
        </Card>

        {/* Learning Guide */}
        <Card className="p-4">
          <h4 className="font-semibold mb-3">🎯 Learning Strategy</h4>
          <div className="space-y-3 text-sm">
            <div className="p-3 bg-green-50 border-l-4 border-green-300">
              <h5 className="font-medium text-green-800">Step 1: Natural Notes</h5>
              <p className="text-green-700">
                Start by memorizing where all the natural notes (C, D, E, F, G, A, B) appear on each string.
              </p>
            </div>
            
            <div className="p-3 bg-blue-50 border-l-4 border-blue-300">
              <h5 className="font-medium text-blue-800">Step 2: Octave Patterns</h5>
              <p className="text-blue-700">
                Notice how the same note appears at different frets. Look for octave relationships between strings.
              </p>
            </div>
            
            <div className="p-3 bg-purple-50 border-l-4 border-purple-300">
              <h5 className="font-medium text-purple-800">Step 3: Reference Points</h5>
              <p className="text-purple-700">
                Use the 12th fret (octave) and 5th fret as reference points to navigate the fretboard.
              </p>
            </div>
          </div>
        </Card>

        {/* Note Finding Tips */}
        <Card className="p-4">
          <h4 className="font-semibold mb-3">💡 Quick Tips</h4>
          <ul className="space-y-2 text-sm">
            <li className="flex items-start gap-2">
              <span className="text-blue-500 font-bold">•</span>
              <span>The 6th string (low E) and 1st string (high E) have the same notes, just 2 octaves apart</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-500 font-bold">•</span>
              <span>The 5th fret of any string equals the open note of the string above it (except G to B string)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-purple-500 font-bold">•</span>
              <span>The 12th fret is always an octave of the open string</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-orange-500 font-bold">•</span>
              <span>Learn one string at a time, then connect patterns across strings</span>
            </li>
          </ul>
        </Card>

        {/* Practice Suggestion */}
        <Card className="p-4 bg-amber-50 border-amber-200">
          <h4 className="font-semibold text-amber-900 mb-2">Ready to Practice?</h4>
          <p className="text-sm text-amber-800">
            Once you've studied the fretboard layout, switch to the <strong>Practice tab</strong> to test your knowledge 
            with interactive challenges!
          </p>
        </Card>
      </CardContent>
    </Card>
  );
};

export default FretboardLearn;